import React from 'react';
import { useIntl } from 'react-intl';
import { FormProvider } from 'react-hook-form';

import Dialog from 'components/mui/Dialog';
import Stack from '@mui/material/Stack';
import FormField from './components/FormField';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';

import { useGlobalAccountFormContext } from './hooks/useGlobalAccountFormContext';
import useAccountForm from './hooks/useAccountForm';

import { globalAccountsConfig } from './config';
import { PaymentProviderName } from 'domain/paySettings/helpers';

type Props = {
  onClose: () => void;
  providerName: PaymentProviderName;
  providerId: string;
  updatingAccount: boolean;
  handleCreate: (accountData: any) => void;
  handleUpdate: (accountData: any) => void;
  selectedAccount: {
    id: string;
  };
  bankAccounts:
    | Array<{
        label: string;
        value: string;
      }>
    | [];
};

const DialogAddGlobalAccount: React.FC<Props> = ({
  onClose,
  handleCreate,
  handleUpdate,
  selectedAccount,
  bankAccounts,
  providerName,
  providerId,
  updatingAccount,
}) => {
  const { formatMessage } = useIntl();

  const { context, isLoading } = useGlobalAccountFormContext({ providerName, providerId, bankAccounts });

  const config = globalAccountsConfig[providerName];

  const { form, isEdit } = useAccountForm({ providerName, selectedAccount });

  const onSubmit = (values: Record<string, unknown>) => {
    if (isEdit) {
      const payload = config.toUpdatePayload
        ? config.toUpdatePayload(values, selectedAccount.id)
        : { ...values, id: selectedAccount.id };

      handleUpdate(payload);

      return;
    }

    handleCreate(config.toCreatePayload ? config.toCreatePayload(values) : values);
  };

  const FieldsComponent = config.FieldsComponent;

  return (
    <FormProvider {...form}>
      <Dialog
        open
        maxWidth={config.dialogMaxWidth ?? 'xs'}
        onClose={onClose}
        onOk={form.handleSubmit(onSubmit)}
        okBtnProps={{
          disabled: !form.formState.isValid,
        }}
        okText={formatMessage({
          id: isEdit ? 'button.save' : 'button.create',
          defaultMessage: isEdit ? 'Save' : 'Create',
        })}
        cancelText={formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        title={formatMessage({
          id: isEdit
            ? 'configurations.company.paymentProviders.detailsModal.accountList.editModal.title'
            : 'configurations.company.paymentProviders.detailsModal.accountList.addModal.title',
          defaultMessage: isEdit ? 'Edit account' : 'Add new account',
        })}
      >
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={2} pt={1}>
            <CircularProgressWithBackdrop isOpen={updatingAccount} />
            {FieldsComponent ? (
              <FieldsComponent context={context} isEdit={isEdit} />
            ) : (
              config.fields.map((field) => (
                <FormField key={field.name} field={field} formContext={context} isEditMode={isEdit} />
              ))
            )}
          </Stack>
        )}
      </Dialog>
    </FormProvider>
  );
};

export default DialogAddGlobalAccount;
